// Vendor
import { Modal } from "flowbite";
import Toastify from "toastify-js";
import { gsap } from "gsap";
import { Draggable } from "gsap/Draggable";
gsap.registerPlugin(Draggable);

// Modules
import { Screensaver } from "./js/Screensaver";
import { Zone } from "./js/Zone";
import { delay, debounce, preloadImages } from "./js/Utils";
import { VideoPreloader } from "./js/VideoPreloader";
import { DragAppIcon } from "./js/DragAppIcon";
import { DragSequential } from "./js/DragSequential";
import { DragFreeform } from "./js/DragFreeform";
import { DragToSpot } from "./js/DragToSpot";
import { ImageSwap } from "./js/ImageSwapper";

// DOM Elements
const sceneIntro = document.getElementById("intro");
const sceneHotspots = document.getElementById("hotspots");
const videoIntro = document.getElementById("hotspotVideo");
const buttonHome = document.getElementById("showCampusButton");
const startCampusButton = document.getElementById("startCampusButton");
const popupsButton = document.getElementById("demoPopupsButton");
const buttonSoftwareValuesBack = document.getElementById("softwareValuesBackButton");
const slideElements = gsap.utils.toArray("[data-slide], [data-demo], #zoneVideos video");
const allVideos = gsap.utils.toArray("video");

// Recycling Demo
let recycleDemoModalInstances = [];
let recycleDemoSequentialDraggables = null;
let recycleDemoOperateMaintainDraggable = null;
let recycleDemoOptimizeRenewDraggable = null;
let recycleDemoFreeformDraggables = null;
let recyclingDemoInitialized = false;

// Wastewater Demo
let wastewaterDemoModalInstances = [];
let wastewaterDemoImageSwapInstances = [];
let wastewaterDemoFreeformDraggables = null;
let wastewaterDemoOperateMaintainDraggable = null;
let wastewaterDemoOperateMaintainDraggable2 = null;
let wastewaterDemoAnim1 = null;
let wastewaterDemoAnim2 = null;
let wastewaterDemoAnim3 = null;
let wastewaterDemoAnim4 = null;
let wastewaterDemoAnim3Status = false;
let wastewaterDemoInitialized = false;

// Dairy Demo
let dairyDemoModalInstances = [];
let dairyDesignBuild1Anim = null;
let dairyDesignBuild3Anim = null;
let dairyDesignBuild5Anim = null;
let dairyDesignBuild6Anim = null;
let dairyDesignBuild7Anim = null;
let dairyDesignBuild8Anim = null;
let dairyOperateMaintain1DragIcon = null;
let dairyOperateMaintain3Anim = null;
let dairyOptimizeRenew2Anim = null;
let dairyOptimizeRenew3Anim = null;
let dairyOptimizeRenew4Anim = null;
let dairyOptimizeRenew5Anim = null;
let dairyDemoInitialized = false;

// Globals
let activeSlide = null;
let modalsDisabled = false;
window.debug = false;

// ----------------------------------------
// Initialise
// ----------------------------------------

// Preload
new VideoPreloader(() => {
  preloadImages().then(() => {
    // Remove Preloader
    delay(1000).then(() =>
      document.querySelector(".preloader-wrapper").remove(),
    );
    // Screensaver
    new Screensaver("#screensaver", "#screensaverButton", 60000);
    // Zones
    gsap.utils.toArray("[data-hotspot]").forEach((el) => new Zone(el));
    // Modals
    initDemoModals();

    // need to stop modal videos playing when modal closed
    gsap.utils.toArray("[data-modal-hide]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        const container = btn.closest(".relative");
        const video = container ? container.querySelector("video") : null;
        if (video) {
          video.pause();
          video.currentTime = 0;
        }
      });
    });

    // Navigation Prev/Next Slide - Only available in software demos!
    gsap.utils.toArray(".btn-nav-control").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        reset();
        revealer(`#${e.target.dataset.link}`);
      });
    });

    // Start App
    if (!window.debug) {
      showFirstSlide();
    } else {
      showDebugSlide();
    }
  });
});

// First Slide
const showFirstSlide = () => {
  [sceneIntro, videoIntro].forEach((elem) => {
    elem.classList.remove("hidden", "opacity-0", "pointer-events-none");
  });
  if (window.debug) {
    console.log("First slide shown reset.");
  }
};

// First Slide (Debug mode)
const showDebugSlide = () => {
  recyclingDemoInitialized = true;
  wastewaterDemoInitialized = true;
  dairyDemoInitialized = true;
  initDairyDemoFunctions();
  initRecyclingDemoDraggables();
  initRecyclingDemoFunctions();
  initRecyclingDemoDraggables();
  initDairyDemoFunctions();
  initWastewaterDemoDraggables();
  initWastewaterDemoFunctions();
  revealer("#recyclingDesignBuildDivider");
};

// ----------------------------------------
// App Methods
// ----------------------------------------

// Reset
const reset = () => {
  // reset slides
  slideElements.forEach((el) => el.classList.add("hidden", "opacity-0", "pointer-events-none"));
  // reset hotspots
  sceneHotspots.classList.add("hidden", "opacity-0", "pointer-events-none");
  // pause any playing videos and reset
  if (activeSlide) {
    const activeContainer = document.querySelector(activeSlide);
    const vids = activeContainer?.querySelectorAll("video");
    vids?.forEach((v) => {
      if (!v.paused) {
        v.pause();
        v.currentTime = 0;
      }
    });
  }
  // reset animations in demos
  resetRecyclingDemo();
  resetWastewaterDemo();
  resetDairyDemo();
  // renable home button
  buttonHome.disabled = false;
  if (window.debug) {
    console.log("Application reset.");
  }
};

// Intro
const playIntro = () => {
  reset();
  showFirstSlide();
  buttonHome.disabled = true;
  sceneIntro.classList.add("hidden", "opacity-0", "pointer-events-none");
  videoIntro.play();
  delay(videoIntro.duration * 1000).then(() => {
    sceneHotspots.classList.remove(
      "hidden",
      "opacity-0",
      "pointer-events-none",
    );
    buttonHome.disabled = false;
  });
  if (window.debug) {
    console.log("Application started.");
  }
};

// Reset
const resetApplication = () => {
  if (window.debug) {
    console.log("Reset Application");
    localStorage.clear();
  }
  reset();
  showFirstSlide();
};

// Revealer
const revealer = (revealElement) => {
  reset();
  if (window.debug) {
    console.log(`Revealing...${revealElement}`);
  }
  if (revealElement) {
    document
      .querySelector(revealElement)
      .classList.remove("hidden", "opacity-0", "pointer-events-none");
    activeSlide = revealElement;
  }

  const category = revealElement.split(/[A-Z0-9]/)[0].replace("#", "");
  // e.g., results in 'recycling', 'wastewater', or 'dairy'

  if (category === "recycling") RecyclingDemoFuncs();
  else if (category === "wastewater") WastewaterDemoFuncs();
  else if (category === "dairy") DairyDemoFuncs();

  // // see if we need to initialise
  // RecyclingDemoFuncs();
  // WastewaterDemoFuncs();
  // DairyDemoFuncs();
};

// ----------------------------------------
// UI
// ----------------------------------------

// Restart
document.querySelectorAll("[data-restart]").forEach((el) => {
  el.addEventListener("click", (e) => {
    playIntro();
  });
});

// Start
startCampusButton.addEventListener("click", (e) => {
  playIntro();
});

// Reset
buttonHome.addEventListener("click", (e) => {
  resetApplication();
});

// Home
buttonSoftwareValuesBack.addEventListener("click", (e) => {
  resetApplication();
});

// Go to slide buttons
gsap.utils.toArray("[data-demo-target]").forEach((el) => {
  el.addEventListener("click", (e) => {
    revealer(el.dataset.demoTarget);
  });
});

// Text Modals
if (popupsButton) {
  popupsButton.addEventListener("change", (e) => {
    modalsDisabled = !e.target.checked;
    if (window.debug) {
      console.log(`Popup Status: ${modalsDisabled}`);
    }
    // Toastify({
    //   text: modalsDisabled ? "Popups Disabled" : "Popups Enabled",
    //   duration: 2000,
    //   gravity: "bottom",
    //   position: "left",
    //   className: "!rounded-lg !shadow !font-bold !text-lg",
    //   style: { background: "#000000" },
    // }).showToast();
  });
}

// ----------------------------------------
// Video Handler
// ----------------------------------------

const setupVideoHandler = (slideId, callback = null) => {
  const container = document.querySelector(slideId);
  if (!container) return;
  const vid = container.querySelector("video");
  if (window.debug) {
    console.log(`setupVideoHandler: Video: ${vid}`);
  }
  if (!vid) {
    console.warn(
      `setupVideoHandler: Could not find video or target element for slideId: ${slideId}`,
    );
    return;
  }
  const onVideoEnd = () => {
    if (typeof callback === "function") {
      callback();
    }
  };
  vid.addEventListener("ended", onVideoEnd);
  vid.play();
};

// ----------------------------------------
// Recycling Demo
// ----------------------------------------

const initRecyclingDemoDraggables = () => {
  recycleDemoOperateMaintainDraggable = new DragAppIcon(
    "#recyclingOperateMaintain1",
    () => {
      if (!modalsDisabled) {
        recycleDemoModalInstances[4].show();
      }
    },
  );
  recycleDemoOptimizeRenewDraggable = new DragAppIcon(
    "#recyclingOptimizeRenew1",
    () => {
      if (!modalsDisabled) {
        recycleDemoModalInstances[5].show();
      }
    },
  );
  recycleDemoSequentialDraggables = new DragSequential(
    ".js-draggables-sequence",
    () => {
      if (!modalsDisabled) {
        recycleDemoModalInstances[1].show();
      }
    },
  );
  // FIX: UPDATE HTML OUTPUT
  recycleDemoFreeformDraggables = new DragFreeform(
    "#recyclingDesignBuild2.js-draggables-freeform",
    () => {
      if (!modalsDisabled) {
        recycleDemoModalInstances[3].show();
      }
    },
  );
};
const resetRecyclingDemo = () => {
  if (recyclingDemoInitialized) {
    recycleDemoOperateMaintainDraggable.reset();
    recycleDemoOptimizeRenewDraggable.reset();
    recycleDemoSequentialDraggables.reset();
    recycleDemoFreeformDraggables.reset();
  }
};
const initRecyclingDemoFunctions = () => {
  if (recyclingDemoInitialized) {
    if (activeSlide == "#recyclingDesignBuild1") {
      if (!modalsDisabled) {
        delay(100).then(() => recycleDemoModalInstances[0].show());
      }
    }
    if (activeSlide == "#recyclingDesignBuild2") {
      if (!modalsDisabled) {
        delay(100).then(() => recycleDemoModalInstances[2].show());
      }
    }
    if (activeSlide == "#recyclingDesignBuild3") {
      setupVideoHandler("#recyclingDesignBuild3");
    }
    if (activeSlide == "#recyclingOperateMaintain2") {
      setupVideoHandler("#recyclingOperateMaintain2");
    }
    if (activeSlide == "#recyclingOptimizeRenew2") {
      setupVideoHandler("#recyclingOptimizeRenew2", () => {
        if (!modalsDisabled) {
          recycleDemoModalInstances[6].show();
        }
      });
    }
  }
};
const RecyclingDemoFuncs = () => {
  if (activeSlide == "#recyclingMenu") {
    if (!recyclingDemoInitialized) {
      initRecyclingDemoDraggables();
      recyclingDemoInitialized = true;
    }
  }
  initRecyclingDemoFunctions();
};

// ----------------------------------------
// Wastewater Demo
// ----------------------------------------

const initWastewaterDemoDraggables = () => {
  let wastewaterDemoAnim3Container = document.querySelector(
    "[data-anim-wastewaterOperateMaintain4]",
  );
  let wastewaterDemoAnim3Img =
    wastewaterDemoAnim3Container.querySelectorAll("img");
  let wastewaterDemoAnim3Button =
    wastewaterDemoAnim3Container.querySelector("button");

  wastewaterDemoAnim3 = gsap
    .timeline({
      paused: true,
      defaults: { duration: 1, ease: "none" },
      onComplete: () => {
        // wastewaterDemoAnim3Status = true;
        delay(2000).then(() => wastewaterDemoModalInstances[6].show());
      },
    })
    .to(wastewaterDemoAnim3Img[0], { opacity: 0, visibility: "hidden" })
    .to(
      wastewaterDemoAnim3Img[1],
      { opacity: 1, visibility: "visible" },
      "-=1",
    );

  wastewaterDemoAnim3Button.addEventListener("click", (e) => {
    e.preventDefault();

    wastewaterDemoAnim3.play(0);

    // if (!wastewaterDemoAnim3Status) {
    //   wastewaterDemoAnim3.play(0);
    // } else {
    //   wastewaterDemoModalInstances[6].show();
    // }
  });

  let wws2 = document.querySelector("[data-wastewater-step-two-anim]");
  let wws2_button = wws2.querySelector("button");
  let wws2_images = wws2.querySelectorAll("img");

  // wastewaterDemoAnim1 = gsap.timeline({
  //   paused: true,
  //   onComplete: () => {
  //     if (!modalsDisabled) {
  //       delay(1000).then(() => wastewaterDemoModalInstances[0].show());
  //     }
  //   },
  // });

  // wastewaterDemoAnim1

  //   .fromTo(
  //     wws2_images[1],
  //     {
  //       y: 0,
  //       x: -250,
  //       opacity: 0,
  //       scale: 0.5,
  //     },
  //     {
  //       transformOrigin: "center center",
  //       y: 0,
  //       x: 0,
  //       duration: 1,
  //       ease: "back.inOut",
  //       opacity: 1,
  //       scale: 1,
  //     }
  //   )
  //   .to(wws2_images[1], {
  //     delay: 1,
  //     y: 0,
  //     x: 250,
  //     duration: 1,
  //     ease: "back.inOut",
  //     opacity: 0,
  //     scale: 0.5,
  //   })

  //   .to(wws2_images[3], {
  //     duration: 0.25,
  //     ease: "none",
  //     opacity: 0,
  //   })
  //   .to(
  //     wws2_images[4],
  //     {
  //       duration: 0.25,
  //       ease: "none",
  //       opacity: 1,
  //     },
  //     "<"
  //   )
  //   .fromTo(
  //     wws2_images[2],
  //     {
  //       y: 0,
  //       x: 250,
  //       opacity: 0,
  //       scale: 0.5,
  //     },
  //     {
  //       transformOrigin: "center center",
  //       y: 0,
  //       x: 0,
  //       duration: 1,
  //       ease: "back.inOut",
  //       opacity: 1,
  //       scale: 1,
  //     }
  //   );

  wastewaterDemoAnim1 = gsap.timeline({
    paused: true,
    onComplete: () => {
      if (!modalsDisabled) {
        delay(1000).then(() => wastewaterDemoModalInstances[0].show());
      }
    },
  });

  wastewaterDemoAnim1

    .fromTo(
      wws2_images[1],
      { y: 0, x: -250, opacity: 0, scale: 0.5 },
      {
        transformOrigin: "center center",
        y: 0,
        x: 0,
        duration: 1,
        ease: "back.inOut",
        opacity: 1,
        scale: 1,
      },
    )
    .fromTo(
      wws2_images[1],
      { y: 0, x: 0, opacity: 1, scale: 1 },
      {
        delay: 1,
        transformOrigin: "center center",
        y: 0,
        x: 250,
        duration: 1,
        ease: "back.inOut",
        scale: 0.5,
        opacity: 0,
      },
    )

    .to(wws2_images[3], {
      duration: 0.25,
      ease: "none",
      opacity: 0,
    })
    .to(
      wws2_images[4],
      {
        duration: 0.25,
        ease: "none",
        opacity: 1,
      },
      "<",
    )
    .fromTo(
      wws2_images[2],
      {
        y: 0,
        x: 250,
        opacity: 0,
        scale: 0.5,
      },
      {
        transformOrigin: "center center",
        y: 0,
        x: 0,
        duration: 1,
        ease: "back.inOut",
        opacity: 1,
        scale: 1,
      },
    );

  wws2_button.addEventListener("click", (e) => {
    wastewaterDemoAnim1.play(0);
  });

  // FIX: UPDATE HTML OUTPUT
  wastewaterDemoFreeformDraggables = new DragFreeform(
    ".js-draggables-freeform-wastewater",
    () => {
      if (!modalsDisabled) {
        wastewaterDemoModalInstances[2].show();
      }
    },
    6,
  );

  let wws4 = document.querySelector("[data-wastewater-step-four-anim]");
  let wws4_button = wws4.querySelector("button");
  let wws4_images = wws4.querySelectorAll("img");
  let wws4_imageContainer = wws4.querySelector(".end");
  let wws4_imageContainer_img1 = wws4_imageContainer.querySelectorAll("img")[0];
  let wws4_imageContainer_img2 = wws4_imageContainer.querySelectorAll("img")[1];

  wastewaterDemoAnim2 = gsap.timeline({
    paused: true,
    onComplete: () => {
      if (!modalsDisabled) {
        wastewaterDemoModalInstances[3].show();
      }
    },
  });

  wastewaterDemoAnim2
    .fromTo(
      wws4_images[2],
      {
        y: 0,
        x: 250,
        opacity: 0,
        scale: 0.5,
      },
      {
        transformOrigin: "center center",
        y: 0,
        x: 0,
        duration: 1,
        ease: "back.inOut",
        opacity: 1,
        scale: 1,
      },
    )
    .fromTo(
      wws4_images[2],
      { y: 0, x: 0, opacity: 1, scale: 1 },
      {
        delay: 1,
        y: 0,
        x: -250,
        duration: 1,
        ease: "back.inOut",
        opacity: 0,
        scale: 0.5,
      },
    )
    .to(wws4_imageContainer_img1, {
      duration: 1,
      ease: "back.inOut",
      opacity: 0,
    })
    .to(
      wws4_imageContainer_img2,
      {
        duration: 1,
        ease: "back.inOut",
        opacity: 1,
      },
      "-=1",
    );

  wws4_button.addEventListener("click", (e) => {
    wastewaterDemoAnim2.play(0);
  });

  let imageSwaps = gsap.utils.toArray("[data-swap-image]");
  imageSwaps.forEach((el) => {
    let instance = new ImageSwap(el, () => {
      if (el.dataset.swapImage == "wastewaterOptimizeRenew2") {
        if (!modalsDisabled) {
          wastewaterDemoModalInstances[7].show();
        }
      }
    });
    wastewaterDemoImageSwapInstances.push(instance);
  });

  wastewaterDemoOperateMaintainDraggable = new DragAppIcon(
    "#wastewaterOperateMaintain2",
    () => {
      // gsap.to(".js-crossbar", { transformOrigin: "50% 50%", scaleY: 0 });
      gsap.fromTo(
        "#wastewaterOperateMaintain2 .js-check",
        { scale: 0, opacity: 0 },
        {
          scale: 1,
          opacity: 1,
          ease: "elastic.inOut",
          duration: 1.4,
          onComplete: () => {
            revealer("#wastewaterOperateMaintain3");
          },
        },
      );
    },
  );

  wastewaterDemoOperateMaintainDraggable2 = new DragAppIcon(
    "#wastewaterOptimizeRenew2_edit",
    () => {
      gsap.fromTo(
        "#wastewaterOptimizeRenew2_edit .js-check",
        { scale: 0, opacity: 0 },
        {
          scale: 1,
          opacity: 1,
          ease: "elastic.inOut",
          duration: 1.4,
        },
      );
    },
  );
};
const resetWastewaterDemo = () => {
  if (wastewaterDemoInitialized) {
    [
      wastewaterDemoAnim1,
      wastewaterDemoAnim2,
      wastewaterDemoAnim3,
      wastewaterDemoAnim4,
    ].forEach((element) => {
      if (element !== null) {
        element.revert();
      }
    });
    wastewaterDemoAnim3Status = false;
    wastewaterDemoImageSwapInstances.forEach((element) => element.reset());
    gsap.set(" [data-demo-target]", { clearProps: "all" });
    gsap.set(".js-check", { clearProps: "all" });
    gsap.set("[data-wasterwater-area]", { opacity: 0, scale: 0.8 });
    wastewaterDemoFreeformDraggables.reset();
    wastewaterDemoOperateMaintainDraggable.reset();
    wastewaterDemoOperateMaintainDraggable2.reset();
  }
};
const initWastewaterDemoFunctions = () => {
  if (wastewaterDemoInitialized) {
    if (activeSlide == "#wastewaterDesignBuild1") {
      gsap.fromTo(
        "[data-wasterwater-area]",
        { opacity: 0, scale: 0.8, transformOrigin: "center center" },
        {
          duration: 0.8,
          ease: "back.inOut",
          opacity: 1,
          transformOrigin: "center center",
          scale: 1,
          stagger: { each: 0.3 },
        },
      );
    }
    if (activeSlide == "#wastewaterDesignBuild3") {
      if (!modalsDisabled) {
        delay(1000).then(() => wastewaterDemoModalInstances[1].show());
      }
    }
    if (activeSlide == "#wastewaterOperateMaintain1") {
      setupVideoHandler("#wastewaterOperateMaintain1");
    }
    if (activeSlide == "#wastewaterOperateMaintain2") {
      if (!modalsDisabled) {
        delay(1000).then(() => wastewaterDemoModalInstances[4].show());
      }
    }
    if (activeSlide == "#wastewaterOperateMaintain3") {
      if (!modalsDisabled) {
        wastewaterDemoModalInstances[5].show();
      }
    }
    if (activeSlide == "#wastewaterOptimizeRenew1") {
      setupVideoHandler("#wastewaterOptimizeRenew1");
    }
    if (activeSlide == "#wastewaterOptimizeRenew4") {
      // get the images from local storage
      let elems = gsap.utils.toArray("[data-wastewater-product] > img");
      elems.forEach((el) => el.remove());
    }
    if (activeSlide == "#wastewaterOptimizeRenew5") {
      let resetDemo = document.querySelector("[data-wastewater-reset]");
      let resetApp = document.querySelector("[data-reset]");
      resetApp.addEventListener("click", (e) => {
        e.preventDefault();
        localStorage.clear();
        // console.clear();
        reset();
        showFirstSlide();
      });
      resetDemo.addEventListener("click", (e) => {
        e.preventDefault();
        localStorage.clear();
        // console.clear();
        reset();
        revealer("#wastewaterMenu");
      });
    }
  }
};
const WastewaterDemoFuncs = () => {
  if (activeSlide == "#wastewaterMenu") {
    if (!wastewaterDemoInitialized) {
      initWastewaterDemoDraggables();
      wastewaterDemoInitialized = true;
    }
  }
  initWastewaterDemoFunctions();
};

// ----------------------------------------
// Dairy Demo
// ----------------------------------------

const initDairyDemoFunctions = () => {
  if (dairyDemoInitialized) {
    // Design / Build
    if (activeSlide == "#dairyDesignBuild1") {
      let dots = gsap.utils.toArray(
        "#dairyDesignBuild1 [data-dairy-animation-dot]",
      );
      let nextButton = gsap.utils.toArray(
        "#dairyDesignBuild1 button[data-demo-target]",
      );

      gsap.set(dots, { scale: 0, opacity: 0 });

      dairyDesignBuild1Anim = gsap.timeline({
        paused: true,
        defaults: { ease: "power3", duration: 1 },
      });
      dairyDesignBuild1Anim.to(dots, { opacity: 1, scale: 1, stagger: 0.4 });
      dairyDesignBuild1Anim.play();
    }
    if (activeSlide == "#dairyDesignBuild3") {
      let dairyDesignBuild3AnimContainer =
        document.querySelector("#dairyDesignBuild3");
      let dairyDesignBuild3AnimImgDefault =
        dairyDesignBuild3AnimContainer.querySelectorAll("[data-asset]")[0];
      let dairyDesignBuild3AnimImgReplaced =
        dairyDesignBuild3AnimContainer.querySelectorAll("[data-asset]")[1];
      let dairyDesignBuild3AnimTriggers =
        dairyDesignBuild3AnimContainer.querySelectorAll("button[data-click]");

      dairyDesignBuild3Anim = gsap.timeline({
        paused: true,
        defaults: { ease: "power3", duration: 1 },
      });
      dairyDesignBuild3Anim
        .to(dairyDesignBuild3AnimTriggers, {
          opacity: 0,
          visibility: "hidden",
          pointerEvents: "none",
        })
        .to(
          dairyDesignBuild3AnimImgDefault,
          { opacity: 0, visibility: "hidden" },
          "-=1",
        )
        .to(
          dairyDesignBuild3AnimImgReplaced,
          { opacity: 1, visibility: "visible" },
          "-=1",
        );

      // buttons
      dairyDesignBuild3AnimTriggers.forEach((btn) => {
        btn.addEventListener("click", (e) => {
          dairyDesignBuild3Anim.play();
        });
      });
    }
    if (activeSlide == "#dairyDesignBuild5") {
      let dairyDemoAnim1Container =
        document.querySelector("#dairyDesignBuild5");
      let dairyDemoAnim1ImageRight =
        dairyDemoAnim1Container.querySelectorAll("[data-asset]")[0];
      let dairyDemoAnim1ImageRightRevealed =
        dairyDemoAnim1Container.querySelectorAll("[data-asset]")[1];
      let dairyDemoAnim1Button =
        dairyDemoAnim1Container.querySelector("[data-trigger]");
      let dairyDemoAnim1Bar =
        dairyDemoAnim1Container.querySelector("[data-bar]");
      let dairyDemoAnim1Tick =
        dairyDemoAnim1Container.querySelector("[data-tick]");

      // timeline
      dairyDesignBuild5Anim = gsap.timeline({
        paused: true,
        defaults: { ease: "power3", duration: 1 },
      });
      dairyDesignBuild5Anim
        .to(dairyDemoAnim1Button, {
          opacity: 0,
          visibility: "hidden",
          pointerEvents: "none",
        })
        .to(dairyDemoAnim1Bar, { width: "100%", duration: 0.8 }, "-=1")
        .to(dairyDemoAnim1Tick, { scale: 1, ease: "back.out", duration: 0.4 })
        .to(dairyDemoAnim1ImageRight, { opacity: 0, visibility: "hidden" })
        .to(
          dairyDemoAnim1ImageRightRevealed,
          { opacity: 1, visibility: "visible" },
          "-=1",
        );

      // button
      dairyDemoAnim1Button.addEventListener("click", (e) => {
        dairyDesignBuild5Anim.play();
      });
    }
    if (activeSlide == "#dairyDesignBuild6") {
      dairyDesignBuild6Anim = new DragToSpot("#dairyDesignBuild6", () => {
        revealer("#dairyDesignBuild7");
      });
    }
    if (activeSlide == "#dairyDesignBuild7") {
      let dairyDesignBuild7AnimContainer =
        document.querySelector("#dairyDesignBuild7");
      let dairyDesignBuild7AnimImgDefault =
        dairyDesignBuild7AnimContainer.querySelectorAll("[data-change]")[0];
      let dairyDesignBuild7AnimImgReplaced =
        dairyDesignBuild7AnimContainer.querySelectorAll("[data-change]")[1];
      let dairyDesignBuild7AnimTrigger =
        dairyDesignBuild7AnimContainer.querySelector("button[data-click]");

      dairyDesignBuild7Anim = gsap.timeline({
        paused: true,
        defaults: { ease: "power3", duration: 1 },
      });

      dairyDesignBuild7Anim
        .to(dairyDesignBuild7AnimImgDefault, {
          opacity: 0,
          visibility: "hidden",
        })
        .to(
          dairyDesignBuild7AnimImgReplaced,
          { opacity: 1, visibility: "visible" },
          "-=1",
        );

      // buttons
      dairyDesignBuild7AnimTrigger.addEventListener("click", (e) => {
        dairyDesignBuild7Anim.play();
      });
    }

    if (activeSlide == "#dairyDesignBuild8") {
      const container = document.querySelector("[data-container]");

      if (container) {
        const products = container.querySelectorAll("[data-product]");
        const zones = container.querySelectorAll("[data-zone]");
        const images = container.querySelectorAll("[data-laptop]");
        const button = container.querySelector("button[data-click]");
        const labels = container.querySelectorAll("[data-label]");

        dairyDesignBuild8Anim = gsap.timeline({
          paused: true,
          defaults: { ease: "power2.out", duration: 1 },
        });

        dairyDesignBuild8Anim.to(labels, { opacity: 0, duration: 0.3 });

        if (products.length === zones.length && products.length > 0) {
          products.forEach((product, index) => {
            const zone = zones[index];

            // Get the relative positions within the container
            const zoneLeft = zone.offsetLeft;
            const zoneTop = zone.offsetTop;
            const productLeft = product.offsetLeft;
            const productTop = product.offsetTop;

            //get the container's position.
            // const containerLeft = doc.offsetLeft;
            // const containerTop = doc.offsetTop;

            //get the product's position relative to the document.
            const productDocumentLeft = product.getBoundingClientRect().left;
            const productDocumentTop = product.getBoundingClientRect().top;

            //get the zone's position relative to the document.
            const zoneDocumentLeft = zone.getBoundingClientRect().left;
            const zoneDocumentTop = zone.getBoundingClientRect().top;

            //calculate the difference.
            const differenceLeft = zoneDocumentLeft - productDocumentLeft;
            const differenceTop = zoneDocumentTop - productDocumentTop;

            // add to anim
            dairyDesignBuild8Anim.to(product, {
              x: differenceLeft,
              y: differenceTop,
              duration: 0.3,
            });
          });

          button.addEventListener("click", (e) => {
            e.preventDefault();
            if (dairyDesignBuild8Anim) {
              dairyDesignBuild8Anim.play();
            }
          });
        } else {
          console.error(
            "The number of products and zones do not match, or there are zero products or zones, within the specified container.",
          );
        }
      } else {
        console.error(
          "Container element '#dairyDesignBuild8 [data-container]' not found.",
        );
      }
    }

    // Operate / Maintain
    if (activeSlide == "#dairyOperateMaintain1") {
      dairyOperateMaintain1DragIcon = new DragAppIcon(
        "#dairyOperateMaintain1",
        () => {
          //  gsap.to(".js-crossbar", { backgroundColor: "#3DCD58" });
          gsap.fromTo(
            "#dairyOperateMaintain1 .js-check",
            { scale: 0, opacity: 0 },
            {
              scale: 1,
              opacity: 1,
              ease: "elastic.inOut",
              duration: 1.4,
            },
          );
        },
      );
    }
    if (activeSlide == "#dairyOperateMaintain3") {
      let dairyOperateMaintain3AnimContainer = document.querySelector(
        "#dairyOperateMaintain3",
      );
      let dairyOperateMaintain3AnimImgDefault =
        dairyOperateMaintain3AnimContainer.querySelectorAll("[data-asset]")[0];
      let dairyOperateMaintain3AnimImgReplaced =
        dairyOperateMaintain3AnimContainer.querySelectorAll("[data-asset]")[1];
      let dairyOperateMaintain3AnimTrigger =
        dairyOperateMaintain3AnimContainer.querySelector("button[data-click]");

      dairyOperateMaintain3Anim = gsap.timeline({
        paused: true,
        defaults: { ease: "power3", duration: 1 },
      });
      dairyOperateMaintain3Anim
        .to(dairyOperateMaintain3AnimTrigger, {
          opacity: 0,
          visibility: "hidden",
          pointerEvents: "none",
        })
        .to(
          dairyOperateMaintain3AnimImgDefault,
          { opacity: 0, visibility: "hidden" },
          "-=1",
        )
        .to(
          dairyOperateMaintain3AnimImgReplaced,
          { opacity: 1, visibility: "visible" },
          "-=1",
        );

      // buttons
      dairyOperateMaintain3AnimTrigger.addEventListener("click", (e) => {
        dairyOperateMaintain3Anim.play();
      });
    }

    // Optimize / Renew
    if (activeSlide == "#dairyOptimizeRenew1") {
      let vid = document.querySelector(activeSlide).querySelector("video");
      vid.play();
    }
    if (activeSlide == "#dairyOptimizeRenew2") {
      dairyOptimizeRenew2Anim = new DragToSpot(
        "#dairyOptimizeRenew2",
        () => {
          delay(500).then(() => revealer("#dairyOptimizeRenew3"));
        },
        1,
      );
    }

    if (activeSlide == "#dairyOptimizeRenew3") {
      let dairyOptimizeRenew3AnimContainer = document.querySelector(
        "#dairyOptimizeRenew3",
      );
      let dairyOptimizeRenew3AnimButton =
        dairyOptimizeRenew3AnimContainer.querySelector("div.clickable");
      let dairyOptimizeRenew3AnimOutput =
        dairyOptimizeRenew3AnimContainer.querySelectorAll("[data-output]")[0];
      let dairyOptimizeRenew3AnimOutput2 =
        dairyOptimizeRenew3AnimContainer.querySelectorAll("[data-output]")[1];
      let dairyOptimizeRenew3Cross =
        dairyOptimizeRenew3AnimContainer.querySelectorAll(".cross");
      let dairyOptimizeRenew3Check =
        dairyOptimizeRenew3AnimContainer.querySelectorAll(".js-check");

      let dairyOptimizeRenew3AnimLaptop =
        dairyOptimizeRenew3AnimContainer.querySelectorAll("[data-asset]")[0];
      let dairyOptimizeRenew3AnimLaptop2 =
        dairyOptimizeRenew3AnimContainer.querySelectorAll("[data-asset]")[1];

      dairyOptimizeRenew3Anim = gsap.timeline({
        paused: true,
        defaults: { ease: "power3", duration: 1 },
      });

      dairyOptimizeRenew3Anim
        .to(dairyOptimizeRenew3AnimOutput, { opacity: 0, visibility: "hidden" })
        .to(
          dairyOptimizeRenew3AnimOutput2,
          { opacity: 1, visibility: "visible" },
          "<",
        )
        .to(dairyOptimizeRenew3Cross, {
          scale: 0,
          opacity: 0,
          visibility: "hidden",
        })
        .to(
          dairyOptimizeRenew3Check,
          { scale: 1, opacity: 1, visibility: "visible" },
          "<",
        )
        .to(dairyOptimizeRenew3AnimLaptop, { opacity: 0, visibility: "hidden" })
        .to(
          dairyOptimizeRenew3AnimLaptop2,
          { opacity: 1, visibility: "visible" },
          "<",
        );

      // buttons
      dairyOptimizeRenew3AnimButton.addEventListener("click", (e) => {
        dairyOptimizeRenew3Anim.play();
      });
    }

    if (activeSlide == "#dairyOptimizeRenew4") {
      let dairyOptimizeRenew4AnimContainer = document.querySelector(
        "#dairyOptimizeRenew4",
      );

      let dairyOptimizeRenew4AnimCheck =
        dairyOptimizeRenew4AnimContainer.querySelector("[data-check]");
      let dairyOptimizeRenew4AnimBar =
        dairyOptimizeRenew4AnimContainer.querySelector("[data-bar]");
      let dairyOptimizeRenew4AnimAsset =
        dairyOptimizeRenew4AnimContainer.querySelector("[data-asset]");
      let dairyOptimizeRenew4AnimLaptop =
        dairyOptimizeRenew4AnimContainer.querySelectorAll("[data-laptop]")[0];
      let dairyOptimizeRenew4AnimLaptop2 =
        dairyOptimizeRenew4AnimContainer.querySelectorAll("[data-laptop]")[1];
      let dairyOptimizeRenew4AnimStartButton =
        dairyOptimizeRenew4AnimContainer.querySelector("div.clickable");

      dairyOptimizeRenew4Anim = gsap.timeline({
        paused: true,
        defaults: { ease: "power3", duration: 1 },
      });

      dairyOptimizeRenew4Anim
        .to(dairyOptimizeRenew4AnimAsset, {
          opacity: 1,
          scale: 1,
          visibility: "visible",
        })
        .to(
          dairyOptimizeRenew4AnimBar,
          { opacity: 1, scaleX: "100%", duration: 0.5 },
          "<",
        )
        .to(
          dairyOptimizeRenew4AnimCheck,
          {
            opacity: 1,
            scale: 1,
            visibility: "visible",
            ease: "back.out",
            duration: 0.3,
          },
          "-=0.15",
        )
        .to(dairyOptimizeRenew4AnimLaptop, {
          opacity: 0,
          visibility: "hidden",
        })
        .to(
          dairyOptimizeRenew4AnimLaptop2,
          { opacity: 1, visibility: "visible" },
          "<",
        );

      // buttons
      dairyOptimizeRenew4AnimStartButton.addEventListener("click", (e) => {
        dairyOptimizeRenew4Anim.play();
      });
    }

    if (activeSlide == "#dairyOptimizeRenew5") {
      if (!modalsDisabled) {
        delay(1000).then(() => dairyDemoModalInstances[0].show());
      }

      // anim
      let dairyOptimizeRenew5AnimContainer = document.querySelector(
        "#dairyOptimizeRenew5",
      );
      let dairyOptimizeRenew5AnimButton =
        dairyOptimizeRenew5AnimContainer.querySelector("[data-click");
      let dairyOptimizeRenew5AnimArrows1 =
        dairyOptimizeRenew5AnimContainer.querySelectorAll("[data-asset]")[0];
      let dairyOptimizeRenew5AnimArrows2 =
        dairyOptimizeRenew5AnimContainer.querySelectorAll("[data-asset]")[1];
      let dairyOptimizeRenew5AnimData =
        dairyOptimizeRenew5AnimContainer.querySelectorAll("[data-asset]")[2];
      let dairyOptimizeRenew5AnimScore =
        dairyOptimizeRenew5AnimContainer.querySelectorAll("[data-asset]")[3];

      let dairyOptimizeRenew5AnimLaptop =
        dairyOptimizeRenew5AnimContainer.querySelectorAll("[data-laptop]")[0];
      let dairyOptimizeRenew5AnimLaptop2 =
        dairyOptimizeRenew5AnimContainer.querySelectorAll("[data-laptop]")[1];

      dairyOptimizeRenew5Anim = gsap.timeline({
        paused: true,
        defaults: { ease: "power3", duration: 0.6 },
        onComplete: () => {
          if (!modalsDisabled) {
            dairyDemoModalInstances[1].show();
          }
        },
      });
      dairyOptimizeRenew5Anim
        .to(dairyOptimizeRenew5AnimButton, {
          opacity: 0,
          visibility: "hidden",
          pointerEvents: "none",
        })
        .to(dairyOptimizeRenew5AnimArrows1, { opacity: 1, x: 0 })
        .to(dairyOptimizeRenew5AnimData, { opacity: 1, scale: 1 })
        .to(dairyOptimizeRenew5AnimData, { opacity: 0, scale: 0 }, "+=1")
        .to(dairyOptimizeRenew5AnimArrows1, { opacity: 0, x: "-10%" })
        .to(dairyOptimizeRenew5AnimArrows2, { opacity: 1, x: 0 })
        .to(dairyOptimizeRenew5AnimScore, { opacity: 1, scale: 1 })

        .to(dairyOptimizeRenew5AnimLaptop, { opacity: 0, visibility: "hidden" })
        .to(
          dairyOptimizeRenew5AnimLaptop2,
          { opacity: 1, visibility: "visible" },
          "-=1",
        );

      // play anim
      dairyOptimizeRenew5AnimButton.addEventListener("click", (e) => {
        dairyOptimizeRenew5Anim.play();
      });
    }
    if (activeSlide == "#dairyOptimizeRenew6") {
      let vid = document.querySelector(activeSlide).querySelector("video");
      vid.addEventListener("ended", (e) => {
        vid.pause();
        vid.currentTime = 0.01;
        if (!modalsDisabled) {
          dairyDemoModalInstances[2].show();
        }
      });
      vid.play();
    }
  }
};
const resetDairyDemo = () => {
  if (dairyDemoInitialized) {
    // reset all animations
    let dairyAnimations = [
      dairyDesignBuild1Anim,
      dairyDesignBuild3Anim,
      dairyDesignBuild5Anim,
      dairyDesignBuild7Anim,
      dairyDesignBuild8Anim,
      dairyOperateMaintain3Anim,
      dairyOptimizeRenew3Anim,
      dairyOptimizeRenew4Anim,
      dairyOptimizeRenew5Anim,
    ];
    dairyAnimations.forEach((element) => {
      if (element !== null) {
        element.revert().pause();
      }
    });

    if (dairyOperateMaintain1DragIcon !== null) {
      dairyOperateMaintain1DragIcon.reset();
    }

    // drg to spot reset
    if (dairyDesignBuild6Anim !== null) {
      dairyDesignBuild6Anim.reset();
    }
    if (dairyOptimizeRenew2Anim !== null) {
      dairyOptimizeRenew2Anim.reset();
    }

    // reset all buttons
    gsap.set("[data-demo-target]", { clearProps: "all" });
  }
};
const DairyDemoFuncs = () => {
  if (activeSlide == "#dairyMenu") {
    if (!dairyDemoInitialized) {
      dairyDemoInitialized = true;
    }
  }
  initDairyDemoFunctions();
};

// ----------------------------------------
// Modals
// ----------------------------------------

const initDemoModals = () => {
  // default config
  let defaults = {
    placement: "center-center",
    backdrop: "static",
    backdropClasses: "bg-dark-backdrop/70 fixed inset-0 z-50",
    closable: true,
  };

  // recycling modals
  gsap.utils.toArray("[data-demo-modal-recycling]").forEach((m) => {
    let instanceOptions = { id: m.id, override: true };
    let options = {
      ...defaults,
      onHide: () => {
        if (m.id == "recyclingDesignBuildModal2") {
          revealer("#recyclingDesignBuild2");
        }
        if (m.id == "recyclingDesignBuildModal4") {
          revealer("#recyclingDesignBuild3");
        }
        if (m.id == "recyclingOperateMaintainModal1") {
          revealer("#recyclingOperateMaintain2");
        }
        if (m.id == "recyclingOptimizeRenewModal1") {
          revealer("#recyclingOptimizeRenew2");
        }
        if (m.id == "recyclingOptimizeRenewModal2") {
          revealer("#recyclingMenu");
        }
      },
    };
    recycleDemoModalInstances.push(new Modal(m, options, instanceOptions));
  });

  // wastewater modals
  gsap.utils.toArray("[data-demo-modal-wastewater]").forEach((m) => {
    let instanceOptions = { id: m.id, override: true };
    let options = {
      ...defaults,
      onHide: () => {
        if (m.id == "wastewaterDesignBuildModal1") {
          revealer("#wastewaterDesignBuild3");
        }
        if (m.id == "wastewaterDesignBuildModal3") {
          revealer("#wastewaterDesignBuild4");
        }
        if (m.id == "wastewaterDesignBuildModal4") {
          revealer("#wastewaterOperateMaintainDivider");
        }
        if (m.id == "wastewaterOperateMaintainModal2") {
          //revealer("#wastewaterOperateMaintain4");
        }
        if (m.id == "wastewaterOperateMaintainModal3") {
          revealer("#wastewaterOptimizeRenewDivider");
        }
        if (m.id == "wastewaterOperateMaintainModal3") {
          revealer("#wastewaterOptimizeRenewDivider");
        }
        if (m.id == "wastewaterOptimizeRenewModal1") {
          revealer("#wastewaterOptimizeRenew3");
        }
        if (m.id == "wastewaterOptimizeRenewModal2") {
          delay(1000).then(() => wastewaterDemoModalInstances[9].show());
        }
        if (m.id == "wastewaterOptimizeRenewModal3") {
          // console.clear();
          localStorage.clear();
          reset();
          showFirstSlide();
        }
      },
    };
    wastewaterDemoModalInstances.push(new Modal(m, options, instanceOptions));
  });

  // dairy modals
  gsap.utils.toArray("[data-demo-modal-dairy]").forEach((m) => {
    let instanceOptions = { id: m.id, override: true };
    let options = {
      ...defaults,
      onHide: () => {
        if (m.id == "dairyOptimizeRenewModal2") {
          revealer("#dairyOptimizeRenew6");
        }
        if (m.id == "dairyOptimizeRenewModal3") {
          localStorage.clear();
          // console.clear();
          reset();
          revealer("#dairyMenu");
        }
      },
    };
    dairyDemoModalInstances.push(new Modal(m, options, instanceOptions));
  });

  if (window.debug) {
    console.log(
      `Initialising Recycling Modals: ${recycleDemoModalInstances.length} found.`,
    );
    console.log(
      `Initialising Wastewater Modals: ${wastewaterDemoModalInstances.length} found.`,
    );
    console.log(
      `Initialising Dairy Modals: ${dairyDemoModalInstances.length} found.`,
    );
  }

  // custom hide button for modals (defaults didnt work for some reason)
  let buttons = gsap.utils.toArray("[data-custom-hide]");
  buttons.forEach((el) => {
    el.addEventListener("click", (e) => {
      e.preventDefault();

      // if the modal has a video
      let modalEl = document.getElementById(el.dataset.customHide);
      let videoEl = modalEl.querySelector("video");
      if (videoEl) {
        videoEl.stop();
        videoEl.currentTime = 0.01;
        videoEl.pause();
      }

      // for demo based modals
      let arrayToUse = "";
      if (el.dataset.customHide.includes("recycling")) {
        arrayToUse = recycleDemoModalInstances;
      }
      if (el.dataset.customHide.includes("wastewater")) {
        arrayToUse = wastewaterDemoModalInstances;
      }
      if (el.dataset.customHide.includes("dairy")) {
        arrayToUse = dairyDemoModalInstances;
      }
      arrayToUse.forEach((m, index) => {
        if (m._instanceId === el.dataset.customHide) {
          arrayToUse[index].hide();
        }
      });

      if (window.debug) {
        console.log(
          `Hiding modal #${el.dataset.customHide} of '${arrayToUse
            .map((m) => m._instanceId)
            .join(", ")}'.`,
        );
      }
    });
  });
};

// ----------------------------------------
// Resize
// ----------------------------------------

window.addEventListener(
  "resize",
  debounce(() => {
    if (!window.debug) {
      reset();
      showFirstSlide();
    }
  }, 250),
);
